import { Dispatch, FC, SetStateAction } from 'react';
import { PreloadedQuery, useLazyLoadQuery, usePreloadedQuery } from 'react-relay';
import { getAssignmentReport, getStudentsWithSkillStates } from '../../graphQLOperations/Assignment';
import { AssignmentStudentsWithSkillStatesQuery } from '../../graphQLOperations/__generated__/AssignmentStudentsWithSkillStatesQuery.graphql';
import { AssignmentItemList } from '../../models/Assignment';
import { Dialog } from 'primereact/dialog';
import { AssignmentMonitoringQuery } from '../../graphQLOperations/__generated__/AssignmentMonitoringQuery.graphql';
import { Button } from 'primereact/button';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  baseAssignment: AssignmentItemList;
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  queryReference: PreloadedQuery<AssignmentMonitoringQuery, Record<string, unknown>>;
}

export const AssignmentNotIncludedStudentsModal: FC<Props> = ({
  baseAssignment,
  visible,
  setVisible,
  queryReference,
}) => {
  const { assignment } = usePreloadedQuery<AssignmentMonitoringQuery>(getAssignmentReport, queryReference);

  const { studentSkillAndEssentialPredecessorsProgress } = useLazyLoadQuery<AssignmentStudentsWithSkillStatesQuery>(
    getStudentsWithSkillStates,
    { classIds: [baseAssignment.classes[0].id], skillId: baseAssignment.skill.id },
    { fetchPolicy: 'network-only' },
  );

  const renderListOfStudents = () => {
    const studentsList: {
      id: string;
      name: string;
      noPredPassed: boolean;
      lastNameForSorted: string;
      publicUserId: string;
    }[] = [];
    const studentIdsInAssignment = assignment?.studentSkillAssignments?.map(item => item?.user?.id);
    studentSkillAndEssentialPredecessorsProgress.forEach(student => {
      if (!studentIdsInAssignment?.includes(student.student.id)) {
        studentsList.push({
          id: student.student.id,
          name: `${student.student.firstName} ${student.student.lastName}`,
          publicUserId: student.student.publicUserId ?? '',
          noPredPassed: student.skillStatuses.slice(1).every(skill => !skill.isPassedOrInferred),
          lastNameForSorted: student.student.lastName ?? '',
        });
      }
    });

    return studentsList.sort((a, b) =>
      a.lastNameForSorted.localeCompare(b.lastNameForSorted, undefined, { sensitivity: 'base' }),
    );
  };

  const studentsList = renderListOfStudents();
  return (
    <>
      <Button onClick={() => setVisible(true)} outlined className="border-none" label="View Not Assigned Students" />
      <Dialog
        footer={
          <div>
            <p className="flex align-items-center">
              <i className="nc_icon-nc_warning ml-1 icon-sm color-yellow mr-2" /> Students who have not completed any of
              the support skills for this skill.
            </p>
          </div>
        }
        visible={visible}
        onHide={() => setVisible(false)}
        header="Not Included Students"
      >
        <div>
          <p className="font-semibold">
            These students are in the class but were not included to receive the assignment:
          </p>
        </div>
        <div>
          {studentsList.length ? (
            studentsList.map(student => (
              <p key={student.id}>
                <MaskedStudentData type={PiiTypes.FullName} student={student}>
                  {student.name}
                </MaskedStudentData>{' '}
                <i className="nc_icon-nc_warning ml-1 color-warning color-yellow" />
              </p>
            ))
          ) : (
            <p className="text-400">No students to display - All students were included in the assignment</p>
          )}
        </div>
      </Dialog>
    </>
  );
};
